import { QueryClient, QueryCache, MutationCache, type DefaultOptions } from "@tanstack/react-query";
import { reportApiError } from "./report";
import { normalizeError } from "./errors";

/**
 * Per-query/mutation metadata understood by the global error handlers.
 * Set `meta: { silentError: true }` to suppress the automatic error toast
 * (e.g. when a screen renders the error inline itself).
 */
export interface QueryMeta extends Record<string, unknown> {
  silentError?: boolean;
  /**
   * The screen renders 403 FORBIDDEN itself (e.g. an owner-only resource),
   * so skip the module-not-allowed redirect for this query.
   */
  handlesForbidden?: boolean;
}

function readSilent(meta: unknown): boolean {
  return Boolean((meta as QueryMeta | undefined)?.silentError);
}

function readHandlesForbidden(meta: unknown): boolean {
  return Boolean((meta as QueryMeta | undefined)?.handlesForbidden);
}

export interface CreateQueryClientOptions {
  defaultOptions?: DefaultOptions;
  /** Redirect to /not-allowed when a GET hits a disabled module (403 FORBIDDEN). Default: true. */
  redirectOnModuleNotAllowed?: boolean;
}

/** Redirect to the not-allowed page when a GET hits a disabled module (403 FORBIDDEN). */
function handleModuleNotAllowed(): void {
  if (typeof window === "undefined") return;
  const { pathname } = window.location;
  if (pathname !== "/not-allowed") {
    window.location.href = "/not-allowed";
  }
}

/**
 * Only retry transient/transport failures. Business and client (4xx) errors
 * are deterministic decisions from the server (validation, forbidden,
 * conflict, rate limit, ...) — retrying them is pointless and can be harmful.
 */
function shouldRetry(failureCount: number, error: unknown): boolean {
  const apiError = normalizeError(error);
  const retryableCodes: ReadonlyArray<typeof apiError.code> = ["network", "timeout", "server"];
  if (!retryableCodes.includes(apiError.code)) return false;
  return failureCount < 2;
}

const DEFAULT_QUERY_OPTIONS: DefaultOptions["queries"] = {
  staleTime: 60_000,
  retry: shouldRetry,
  refetchOnWindowFocus: false,
};

/**
 * Create a {@link QueryClient} pre-wired with global API error handling.
 *
 * Every failed query and mutation flows through {@link reportApiError},
 * which normalizes the error and shows a deduplicated, localized toast —
 * so individual screens no longer need their own boilerplate error toasts.
 */
export function createQueryClient(overrides: CreateQueryClientOptions = {}): QueryClient {
  const redirectOnModuleNotAllowed = overrides.redirectOnModuleNotAllowed ?? true;

  return new QueryClient({
    queryCache: new QueryCache({
      onError: (error, query) => {
        const apiError = normalizeError(error);
        // Module not enabled for this org — redirect instead of a toast (GET only).
        if (apiError.code === "forbidden" && apiError.errorCode === "FORBIDDEN") {
          if (redirectOnModuleNotAllowed && !readHandlesForbidden(query.meta)) {
            handleModuleNotAllowed();
          }
          reportApiError(apiError, { silent: true });
          return;
        }
        // Org not ACTIVE — VendorZoneGuard routes by /status; never toast this.
        if (apiError.errorCode === "VENDOR_ORG_NOT_ACTIVE") {
          reportApiError(apiError, { silent: true });
          return;
        }
        reportApiError(error, { silent: readSilent(query.meta) });
      },
    }),
    mutationCache: new MutationCache({
      onError: (error, _variables, _context, mutation) => {
        const apiError = normalizeError(error);
        // Org not ACTIVE — zone guard owns routing; suppress the global toast.
        if (apiError.errorCode === "VENDOR_ORG_NOT_ACTIVE") {
          reportApiError(apiError, { silent: true });
          return;
        }
        // 404 on a mutation means the submitted resource wasn't found; the
        // screen is expected to render the message inline. Only surface
        // notFound toasts for queries (GETs) where no inline handler exists.
        const silent = readSilent(mutation.meta) || apiError.code === "notFound";
        reportApiError(apiError, { silent });
      },
    }),
    defaultOptions: {
      ...overrides.defaultOptions,
      queries: {
        ...DEFAULT_QUERY_OPTIONS,
        ...overrides.defaultOptions?.queries,
      },
    },
  });
}
